/**
 * T10 - /report-issue form (M1).
 *
 * POST /api/report-issue (stub log to D1 feedback_log table on backend).
 * Auto-fills message_id from ?message_id= query param if present.
 *
 * Spec: F-FE-CHAT.md M1 report-issue surface.
 */

import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

type IssueType =
  | 'AI tra loi sai'
  | 'AI dua tu van khong dung'
  | 'Loi giao dien'
  | 'PII leak'
  | 'Khac';

const ISSUE_TYPES: IssueType[] = [
  'AI tra loi sai',
  'AI dua tu van khong dung',
  'Loi giao dien',
  'PII leak',
  'Khac',
];

export function ReportIssueRoute() {
  const [searchParams] = useSearchParams();
  const initialMessageId = searchParams.get('message_id') ?? '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [issueType, setIssueType] = useState<IssueType>('AI tra loi sai');
  const [description, setDescription] = useState('');
  const [messageId, setMessageId] = useState(initialMessageId);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!description.trim()) {
      setError('Vui lòng mô tả vấn đề.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const resp = await fetch('/api/report-issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || null,
          email: email.trim() || null,
          issue_type: issueType,
          description: description.trim().slice(0, 2000),
          message_id: messageId.trim() || null,
        }),
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Không gửi được báo cáo, vui lòng thử lại.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <main
        style={{
          maxWidth: 560,
          margin: '0 auto',
          padding: '48px 16px',
          fontFamily: 'Inter, system-ui, sans-serif',
          textAlign: 'center',
        }}
      >
        <h1 style={{ color: '#1B3B6F', fontFamily: '"Source Serif Pro", Georgia, serif' }}>
          Cam on ban!
        </h1>
        <p style={{ color: '#475569', fontSize: 15 }}>
          Bao cao cua ban da duoc ghi nhan. Founder se xem xet trong vong 72 gio.
        </p>
        <a
          href="/ask"
          style={{
            display: 'inline-block',
            marginTop: 16,
            background: '#1B3B6F',
            color: '#fff',
            padding: '10px 22px',
            borderRadius: 8,
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          Quay lai hoi tiep
        </a>
      </main>
    );
  }

  return (
    <main
      style={{
        maxWidth: 640,
        margin: '0 auto',
        padding: '32px 16px',
        fontFamily: 'Inter, system-ui, sans-serif',
        color: '#0f172a',
      }}
    >
      <h1
        style={{
          color: '#1B3B6F',
          fontFamily: '"Source Serif Pro", Georgia, serif',
          marginBottom: 8,
        }}
      >
        Bao cao van de
      </h1>
      <p style={{ color: '#475569', fontSize: 14, marginBottom: 24 }}>
        Bao cao se duoc luu tru va xem xet boi founder. Khong chia se thong tin
        ca nhan voi ben thu ba.
      </p>

      <form onSubmit={handleSubmit} aria-label="Form bao cao van de Asklaw">
        <Field label="Ho ten (tuy chon)">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            aria-label="Ho ten"
            style={inputStyle}
          />
        </Field>

        <Field label="Email (tuy chon - de chung toi phan hoi)">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={200}
            aria-label="Dia chi email"
            style={inputStyle}
          />
        </Field>

        <Field label="Loai loi *">
          <select
            value={issueType}
            onChange={(e) => setIssueType(e.target.value as IssueType)}
            aria-label="Loai loi"
            required
            style={inputStyle}
          >
            {ISSUE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Mo ta chi tiet *">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            required
            rows={6}
            aria-label="Mo ta chi tiet van de"
            style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
            placeholder="Vui lòng cung cấp chi tiết: câu hỏi bạn đã hỏi, câu trả lời AI sai chỗ nào, mong đợi của bạn..."
          />
          <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right' }}>
            {description.length}/2000
          </div>
        </Field>

        <Field label="Message ID (tuy chon)">
          <input
            type="text"
            value={messageId}
            onChange={(e) => setMessageId(e.target.value)}
            maxLength={64}
            aria-label="Message ID"
            style={inputStyle}
            placeholder="vd: lay tu URL ?message_id=..."
          />
        </Field>

        {error && (
          <div
            role="alert"
            style={{
              background: '#fee2e2',
              color: '#991b1b',
              padding: '10px 12px',
              borderRadius: 8,
              fontSize: 14,
              margin: '12px 0',
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          aria-label="Gui bao cao"
          style={{
            background: submitting ? '#94a3b8' : '#1B3B6F',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 600,
            cursor: submitting ? 'not-allowed' : 'pointer',
            marginTop: 16,
            minHeight: 44,
          }}
        >
          {submitting ? 'Đang gửi...' : 'Gửi báo cáo'}
        </button>
      </form>
    </main>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  minHeight: 44,
  color: '#0f172a',
  background: '#ffffff',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 14, fontSize: 14, color: '#0f172a' }}>
      <span style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}
